import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { Lock } from 'lucide-react';
import { ReportHeader } from '../../components/ReportHeader';
import { FilterPanel } from '../../components/FilterPanel';
import { SortableTable } from '../../components/SortableTable';
import { ExportButton } from '../../components/ExportButton';
import { Pagination } from '../../components/Pagination';

const API = process.env.REACT_APP_BACKEND_URL;

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(n)  { return (n ?? 0).toLocaleString(); }
function fmtDec(v, dp = 1) { return Number(v ?? 0).toFixed(dp); }
function fmtPct(v) { return `${Number(v ?? 0).toFixed(1)}%`; }

// ── KPI card ──────────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent, testId }) {
  return (
    <div
      data-testid={testId}
      className="stat-card"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value">{value}</div>
      {sub && (
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 style={{
      fontSize: 15, fontWeight: 700, color: '#1e293b',
      margin: '24px 0 10px', letterSpacing: '-0.01em',
    }}>
      {children}
    </h2>
  );
}

// ── Column definitions ─────────────────────────────────────────────────────────

const TOP_ACCEL_COLS = [
  { key: 'name',        label: 'Accelerator',    sortable: true, maxWidth: 380, wrap: true },
  { key: 'launches',    label: 'Launches',        sortable: true, align: 'right' },
  { key: 'unique_users',label: 'Unique Learners', sortable: true, align: 'right' },
];

const SURVEY_COLS = [
  { key: 'name',                   label: 'Name',             sortable: true },
  { key: 'title',                  label: 'Title',            sortable: true },
  { key: 'course_name',            label: 'Accelerator',      sortable: true },
  { key: 'weight',                 label: 'Rating',           sortable: true, align: 'right' },
  { key: 'application_of_learning',label: 'Application',      sortable: true, maxWidth: 240, wrap: true },
  { key: 'created_at',             label: 'Date',             sortable: true },
];

const SURVEY_EXPORT_COLS = [
  { key: 'name',                   label: 'Name' },
  { key: 'title',                  label: 'Title' },
  { key: 'course_name',            label: 'Accelerator' },
  { key: 'weight',                 label: 'Rating' },
  { key: 'response',               label: 'Response' },
  { key: 'application_of_learning',label: 'Application of Learning' },
  { key: 'created_at',             label: 'Date' },
];

const READINESS_COLS = null;   // tab removed
const FEEDBACK_COLS = null;    // tab removed
const FEEDBACK_EXPORT_COLS = null; // tab removed

const TABS = ['top-accelerators', 'survey-insights'];
const TAB_LABELS = {
  'top-accelerators': 'Top Accelerators',
  'survey-insights':  'Survey Insights',
};

// ── Main page ─────────────────────────────────────────────────────────────────
export default function DealReadyAcceleratorReport() {
  // Period
  const [fyLabel,    setFyLabel]    = useState('');
  const [startDate,  setStartDate]  = useState('');
  const [endDate,    setEndDate]    = useState('');

  // KPIs
  const [kpis,       setKpis]        = useState(null);
  const [kpisLoading, setKpisLoading] = useState(false);

  // Trend
  const [trendData,  setTrendData]  = useState([]);
  const [trendLoading, setTrendLoading] = useState(false);

  // Tab
  const [activeTab,  setActiveTab]  = useState('top-accelerators');

  // Top Accelerators
  const [topAccel,      setTopAccel]      = useState([]);
  const [topLoading,    setTopLoading]    = useState(false);
  const [topSortKey,    setTopSortKey]    = useState('launches');
  const [topSortDir,    setTopSortDir]    = useState('desc');

  // Survey Insights
  const [surveyData,   setSurveyData]    = useState({ data: [], total: 0 });
  const [surveyLoading, setSurveyLoading] = useState(false);
  const [surveyPage,    setSurveyPage]   = useState(1);
  const [surveyPageSize, setSurveyPageSize] = useState(50);
  const [surveySortKey, setSurveySortKey] = useState('created_at');
  const [surveySortDir, setSurveySortDir] = useState('desc');
  const [surveyFilters, setSurveyFilters] = useState({ course_name: [], search: '' });
  const [appliedSurveyFilters, setAppliedSurveyFilters] = useState({ course_name: [], search: '' });
  const [courseNames,   setCourseNames]  = useState([]);

  // PII — read from /api/auth/me (fail-closed default: false)
  const [piiAllowed, setPiiAllowed] = useState(false);

  // ── Load course names for survey filter + pii_allowed ───────────────────
  useEffect(() => {
    axios.get(`${API}/api/deal-ready/survey-course-names`, { withCredentials: true })
      .then(({ data }) => setCourseNames(data))
      .catch(() => {});
    axios.get(`${API}/api/auth/me`, { withCredentials: true })
      .then(r => setPiiAllowed(r.data?.pii_allowed === true))
      .catch(() => setPiiAllowed(false));
  }, []);

  // ── KPIs + Trend on period change ─────────────────────────────────────────
  useEffect(() => {
    if (!fyLabel) return;
    const params = { fy_label: fyLabel, start_date: startDate, end_date: endDate };

    setKpisLoading(true);
    setTrendLoading(true);

    Promise.all([
      axios.get(`${API}/api/deal-ready/kpis`,  { params, withCredentials: true }),
      axios.get(`${API}/api/deal-ready/trend`, { params, withCredentials: true }),
    ])
      .then(([kRes, tRes]) => {
        setKpis(kRes.data);
        setTrendData(tRes.data);
      })
      .catch(console.error)
      .finally(() => { setKpisLoading(false); setTrendLoading(false); });
  }, [fyLabel, startDate, endDate]);

  // ── Top Accelerators ──────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'top-accelerators' || !fyLabel) return;
    setTopLoading(true);
    axios.get(`${API}/api/deal-ready/top-accelerators`, {
      params: { fy_label: fyLabel, start_date: startDate, end_date: endDate,
                sort_by: topSortKey, sort_dir: topSortDir, limit: 20 },
      withCredentials: true,
    })
      .then(({ data }) => setTopAccel(data))
      .catch(console.error)
      .finally(() => setTopLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLabel, startDate, endDate, topSortKey, topSortDir]);

  // ── Survey Insights ───────────────────────────────────────────────────────
  useEffect(() => {
    if (activeTab !== 'survey-insights' || !fyLabel) return;
    setSurveyLoading(true);
    const { course_name, search } = appliedSurveyFilters;
    axios.get(`${API}/api/deal-ready/survey-insights`, {
      params: {
        fy_label: fyLabel, start_date: startDate, end_date: endDate,
        page: surveyPage, page_size: surveyPageSize,
        sort_by: surveySortKey, sort_dir: surveySortDir,
        ...(course_name.length ? { course_name: course_name.join(',') } : {}),
        ...(search ? { search } : {}),
      },
      withCredentials: true,
    })
      .then(({ data }) => setSurveyData({ data: data.data, total: data.total }))
      .catch(console.error)
      .finally(() => setSurveyLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, fyLabel, startDate, endDate, surveyPage, surveyPageSize,
      surveySortKey, surveySortDir,
      appliedSurveyFilters.course_name, appliedSurveyFilters.search]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handlePeriodChange = useCallback(({ fyLabel: fy, startDate: s, endDate: e }) => {
    if (!fy) return;
    setFyLabel(fy);
    setStartDate(s || '');
    setEndDate(e || '');
    setSurveyPage(1);
  }, []);

  function handleTopSort(sorts) {
    const s = sorts[0] || { key: 'launches', direction: 'desc' };
    setTopSortKey(s.key);
    setTopSortDir(s.direction);
  }

  function handleSurveySort(sorts) {
    const s = sorts[0] || { key: 'created_at', direction: 'desc' };
    setSurveySortKey(s.key);
    setSurveySortDir(s.direction);
    setSurveyPage(1);
  }

  // ── Export helpers ────────────────────────────────────────────────────────
  const fetchAllSurvey = useCallback(async () => {
    const { course_name, search } = appliedSurveyFilters;
    const { data } = await axios.get(`${API}/api/deal-ready/survey-insights`, {
      params: {
        fy_label: fyLabel, start_date: startDate, end_date: endDate,
        page: 1, page_size: 10000,
        sort_by: surveySortKey, sort_dir: surveySortDir,
        ...(course_name.length ? { course_name: course_name.join(',') } : {}),
        ...(search ? { search } : {}),
      },
      withCredentials: true,
    });
    return data.data;
  }, [fyLabel, startDate, endDate, surveySortKey, surveySortDir, appliedSurveyFilters]);

  // ── Survey filter config ──────────────────────────────────────────────────
  const surveyFilterConfig = [
    { key: 'course_name', label: 'Accelerator', type: 'multiselect', options: courseNames },
    { key: 'search',      label: 'Search',      type: 'text-search' },
  ];

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <>
      <ReportHeader
        reportTitle="Deal Ready Accelerators"
        primaryTable="accelerator_launch_analytics"
        onChange={handlePeriodChange}
      />

      <div data-testid="deal-ready-dashboard" style={{ padding: '0 16px 32px' }}>

        {/* KPI cards */}
        {kpisLoading && !kpis && (
          <div style={{ color: '#64748b', fontSize: 14, padding: '40px 0', textAlign: 'center' }}>
            Loading accelerator data…
          </div>
        )}

        {kpis && (
          <>
            <SectionTitle>Overview — {fyLabel}</SectionTitle>
            <div
              data-testid="deal-ready-kpi-cards"
              className="stats-grid"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
            >
              <KpiCard
                testId="kpi-total-accelerators"
                label="Accelerators"
                value={fmt(kpis.total_accelerators)}
                accent="#6366f1"
              />
              <KpiCard
                testId="kpi-total-launches"
                label="Total Launches"
                value={fmt(kpis.total_launches)}
                accent="#3b82f6"
              />
              <KpiCard
                testId="kpi-unique-learners"
                label="Unique Learners"
                value={fmt(kpis.unique_learners)}
                accent="#0891b2"
              />
              <KpiCard
                testId="kpi-survey-responses"
                label="Survey Responses"
                value={fmt(kpis.total_survey_responses)}
                sub={`${fmtPct(kpis.survey_response_rate)} response rate`}
                accent="#f59e0b"
              />
              <KpiCard
                testId="kpi-avg-survey-weight"
                label="Avg Survey Rating"
                value={fmtDec(kpis.avg_survey_weight)}
                accent="#22c55e"
              />
              <KpiCard
                testId="kpi-avg-recommend"
                label="Avg Recommend Rating"
                value={fmtDec(kpis.avg_recommend_rating)}
                accent="#8b5cf6"
              />
            </div>
          </>
        )}

        {/* Monthly trend */}
        {trendData.length > 0 && (
          <>
            <SectionTitle>Monthly Trend</SectionTitle>
            <div className="report-card" data-testid="deal-ready-trend-chart" style={{ padding: '20px 16px 8px' }}>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={trendData} margin={{ top: 4, right: 24, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={44} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e2e8f0' }} />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} iconType="circle" />
                  <Line type="monotone" dataKey="launches"         name="Launches"          stroke="#3b82f6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  <Line type="monotone" dataKey="survey_responses" name="Survey Responses"  stroke="#f59e0b" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {/* Tabs */}
        {kpis && (
          <>
            {/* Tab bar */}
            <div
              data-testid="deal-ready-tabs"
              style={{ borderBottom: '2px solid #e2e8f0', display: 'flex', gap: 0, marginTop: 28 }}
            >
              {TABS.map(tab => (
                <button
                  key={tab}
                  data-testid={`tab-${tab}`}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '8px 20px', fontSize: 13, fontWeight: 600,
                    border: 'none', background: 'none', cursor: 'pointer',
                    borderBottom: activeTab === tab ? '2px solid #6366f1' : '2px solid transparent',
                    color: activeTab === tab ? '#6366f1' : '#64748b',
                    marginBottom: -2,
                    transition: 'color 0.15s',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {TAB_LABELS[tab]}
                </button>
              ))}
            </div>

            {/* ── Top Accelerators ── */}
            {activeTab === 'top-accelerators' && (
              <div data-testid="tab-content-top-accelerators" style={{ marginTop: 16 }}>
                <div className="report-card">
                  <SortableTable
                    columns={TOP_ACCEL_COLS}
                    data={topAccel}
                    loading={topLoading}
                    defaultSort={{ key: topSortKey, direction: topSortDir }}
                    onSortChange={handleTopSort}
                  />
                </div>
              </div>
            )}

            {/* ── Survey Insights ── */}
            {activeTab === 'survey-insights' && (
              <div data-testid="tab-content-survey-insights" style={{ marginTop: 16 }}>
                <div style={{ marginBottom: 10 }}>
                  <FilterPanel
                    filterConfig={surveyFilterConfig}
                    activeFilters={surveyFilters}
                    onChange={next => {
                      setSurveyFilters(next);
                      setAppliedSurveyFilters(next);
                      setSurveyPage(1);
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                  <ExportButton
                    fetchAllFn={fetchAllSurvey}
                    filename={`survey-insights_${fyLabel}`}
                    columns={piiAllowed ? SURVEY_EXPORT_COLS : SURVEY_EXPORT_COLS.filter(c => c.key !== 'name')}
                    label="Export Survey Data"
                  />
                </div>
                <div className="report-card">
                  <SortableTable
                    columns={piiAllowed ? SURVEY_COLS : [
                      {
                        key: '_name_locked',
                        label: (
                          <span
                            data-testid="survey-name-col-locked"
                            title="NAME column is hidden — not available for your role"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, opacity: 0.45 }}
                          >
                            <Lock size={11} />
                            <span style={{ textDecoration: 'line-through', fontSize: 10, letterSpacing: '0.05em' }}>NAME</span>
                          </span>
                        ),
                        sortable: false,
                        render: () => null,
                      },
                      ...SURVEY_COLS.filter(c => c.key !== 'name'),
                    ]}
                    data={surveyData.data}
                    loading={surveyLoading}
                    defaultSort={{ key: surveySortKey, direction: surveySortDir }}
                    onSortChange={handleSurveySort}
                  />
                  <Pagination
                    totalCount={surveyData.total}
                    pageSize={surveyPageSize}
                    currentPage={surveyPage}
                    onPageChange={setSurveyPage}
                    onPageSizeChange={ps => { setSurveyPageSize(ps); setSurveyPage(1); }}
                  />
                </div>
              </div>
            )}

          </>
        )}
      </div>
    </>
  );
}
